import { IconHeart, IconWand, IconMoodSmile } from "@tabler/icons-react";
import { memo, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { Controller } from "react-hook-form";
import EmojiPickerWrapper from "../../EmojiPIcker";

const StepInteractions = memo(
  ({ watch, setValue, handleAIFill, MICRO_INTERACTIONS, control }) => {
    // Estados para controlar os emoji pickers de cada textarea
    const [showEmojiPickers, setShowEmojiPickers] = useState({});
    const textareaRefs = useRef({});
    const emojiPickerRefs = useRef({});
    
    // ✅ CORREÇÃO: Estados locais para sincronização com IA
    const [localTextareaValues, setLocalTextareaValues] = useState({});
    const [isInitialized, setIsInitialized] = useState(false);
    const lastFormValuesRef = useRef({});

    // ✅ CORREÇÃO: Otimizar com useMemo para evitar recriação desnecessária
    const allInteractionValues = useMemo(() => {
      const values = {};
      Object.keys(MICRO_INTERACTIONS).forEach(key => {
        values[key] = watch(`interactions.${key}.value`);
      });
      return values;
    }, [watch, MICRO_INTERACTIONS]);
    
    // ✅ CORREÇÃO: Inicializar valores locais uma única vez
    useEffect(() => {
      if (!isInitialized) {
        Object.keys(MICRO_INTERACTIONS).forEach(key => {
          const currentValue = allInteractionValues[key];
          setLocalTextareaValues(prev => ({
            ...prev,
            [key]: currentValue || MICRO_INTERACTIONS[key].defaultValue || ""
          }));
          lastFormValuesRef.current[key] = currentValue || MICRO_INTERACTIONS[key].defaultValue || "";
        });
        setIsInitialized(true);
      }
    }, [isInitialized, MICRO_INTERACTIONS, allInteractionValues]);
    
    // ✅ CORREÇÃO: Sincronizar com mudanças da IA (igual ao dynamicFlowStep.js)
    useEffect(() => {
      if (isInitialized) {
        Object.keys(MICRO_INTERACTIONS).forEach(key => {
          const currentFormValue = allInteractionValues[key];
          if (currentFormValue !== undefined) {
            // Verificar se o valor do formulário mudou externamente (pela IA)
            if (currentFormValue !== lastFormValuesRef.current[key] && currentFormValue !== localTextareaValues[key]) {
              console.log(`🔄 Sincronizando valor externo para interactions.${key}:`, {
                fromForm: currentFormValue,
                currentLocal: localTextareaValues[key],
                lastForm: lastFormValuesRef.current[key]
              });
              setLocalTextareaValues(prev => ({
                ...prev,
                [key]: currentFormValue || ""
              }));
            }
            lastFormValuesRef.current[key] = currentFormValue || "";
          }
        });
      }
    }, [isInitialized, allInteractionValues, localTextareaValues, MICRO_INTERACTIONS]);

    // ✅ CORREÇÃO: Handlers para textarea com sincronização local
    const handleTextareaChange = useCallback((key, value) => {
      setLocalTextareaValues(prev => ({
        ...prev,
        [key]: value
      }));
    }, []);

    const handleTextareaBlur = useCallback((key, value) => {
      setValue(`interactions.${key}.value`, value, {
        shouldValidate: false,
        shouldDirty: true,
      });
      lastFormValuesRef.current[key] = value;
    }, [setValue]);

    // Função para inserir emoji em um textarea específico
    const handleEmojiSelect = useCallback(
      (key, emoji) => {
        const textarea = textareaRefs.current[key];
        if (textarea) {
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const currentValue = localTextareaValues[key] || "";

          // Inserir emoji na posição do cursor
          const newValue =
            currentValue.substring(0, start) +
            emoji +
            currentValue.substring(end);

          // Calcular nova posição do cursor
          const newCursorPosition = start + emoji.length;

          // ✅ CORREÇÃO: Atualizar estado local e formulário
          setLocalTextareaValues(prev => ({
            ...prev,
            [key]: newValue
          }));
          setValue(`interactions.${key}.value`, newValue, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
          lastFormValuesRef.current[key] = newValue;

          // Atualizar posição do cursor
          setTimeout(() => {
            if (textarea) {
              textarea.value = newValue;
              textarea.focus();
              textarea.setSelectionRange(newCursorPosition, newCursorPosition);
            }
          }, 0);
        }
      },
      [setValue, localTextareaValues]
    );

    // Função para alternar o emoji picker de um campo específico
    const toggleEmojiPicker = useCallback((key, e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowEmojiPickers((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    }, []);

    // Fechar emoji picker quando clicar fora
    useEffect(() => {
      const handleClickOutside = (event) => {
        Object.keys(showEmojiPickers).forEach((key) => {
          if (
            showEmojiPickers[key] &&
            emojiPickerRefs.current[key] &&
            !emojiPickerRefs.current[key].contains(event.target) &&
            !event.target.closest(`.emoji-picker-button-${key}`)
          ) {
            setShowEmojiPickers((prev) => ({
              ...prev,
              [key]: false,
            }));
          }
        });
      };

      const hasOpenPickers = Object.values(showEmojiPickers).some(Boolean);
      if (hasOpenPickers) {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }, [showEmojiPickers]);

    // Fechar todos os emoji pickers quando o componente for desmontado
    useEffect(() => {
      return () => {
        setShowEmojiPickers({});
      };
    }, []);
    return (
      <div>
        <div className="d-flex align-items-center mb-4">
          <IconHeart className="me-2 text-primary" size={24} />
          <h4 className="mb-0">Micro-Interações Humanizadas</h4>
        </div>

        <Alert variant="info" className="mb-4">
          Configure micro-interações para tornar seu agente mais humanizado e
          natural. Estas configurações aparecerão no final do prompt.
        </Alert>

        {Object.entries(MICRO_INTERACTIONS).map(([key, config]) => (
          <Card key={key} className="mb-3">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <Controller
                  name={`interactions.${key}.enabled`}
                  control={control}
                  defaultValue={false}
                  render={({ field: { value, onChange, ...field } }) => (
                    <Form.Check
                      {...field}
                      type="checkbox"
                      id={`interaction-${key}`}
                      label={<strong>{config.label}</strong>}
                      checked={value || false}
                      onChange={(e) => {
                        onChange(e.target.checked);
                        console.log(`🔄 Checkbox ${key} alterado para:`, e.target.checked);
                      }}
                    />
                  )}
                />
                {watch(`interactions.${key}.enabled`) && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() =>
                      handleAIFill(`interactions.${key}.value`, {
                        label: config.label,
                        description: `Configuração para ${config.label.toLowerCase()}`,
                      })
                    }
                  >
                    <IconWand className="me-1" size={14} />
                    Preencher com IA
                  </Button>
                )}
              </div>

              {watch(`interactions.${key}.enabled`) && (
                <Form.Group>
                  <div className="d-flex justify-content-between mb-1">
                    <Form.Label>Configuração</Form.Label>
                    {/* Botão do emoji picker */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className={`emoji-picker-button-${key} p-1`}
                      onClick={(e) => toggleEmojiPicker(key, e)}
                      type="button"
                    >
                      <IconMoodSmile size={16} />
                    </Button>
                  </div>

                  <div style={{ position: "relative" }}>
                    {/* ✅ CORREÇÃO: Textarea controlado com sincronização */}
                    <Form.Control
                      as="textarea"
                      rows={key === "emojis" ? 8 : 3}
                      value={localTextareaValues[key] || config.defaultValue || ""}
                      onChange={(e) => handleTextareaChange(key, e.target.value)}
                      onBlur={(e) => handleTextareaBlur(key, e.target.value)}
                      placeholder={config.placeholder}
                      ref={(el) => {
                        if (el) {
                          textareaRefs.current[key] = el;
                        }
                      }}
                    />

                    {/* Emoji Picker */}
                    {showEmojiPickers[key] && (
                      <div
                        ref={(el) => {
                          if (el) {
                            emojiPickerRefs.current[key] = el;
                          }
                        }}
                        style={{
                          position: "absolute",
                          top: "100%",
                          right: 0,
                          zIndex: 1050,
                          marginTop: "5px",
                        }}
                      >
                        <EmojiPickerWrapper
                          onEmojiSelect={(emoji) => handleEmojiSelect(key, emoji)}
                          onClose={() =>
                            setShowEmojiPickers((prev) => ({
                              ...prev,
                              [key]: false,
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>
                </Form.Group>
              )}
            </Card.Body>
          </Card>
        ))}

        {Object.keys(MICRO_INTERACTIONS).every(
          (key) => !watch(`interactions.${key}.enabled`)
        ) && (
          <div className="text-center py-5 text-muted">
            <IconHeart size={48} className="mb-3" />
            <p>Nenhuma micro-interação habilitada.</p>
            <p>Ative as opções acima para humanizar seu agente.</p>
          </div>
        )}
      </div>
    );
  }
);

StepInteractions.displayName = "StepInteractions";

export default StepInteractions;
